import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { CorporateUserViewModel } from '../types/corporate-user';
import type {
  CorporateUserProfileUpdateRequest,
  CorporateUserProfileViewModel,
} from '../types/corporate';
import { apiClient } from '../client/client';
import { buildPaginationParams } from '../client/queryParams';
import { buildOrderByParams } from '../client/queryParams';

export interface CorporateUserQuery {
  Email?: string | null;
  CompanyName?: string | null;
  TaxNumber?: string | null;
  IsEmailConfirmed?: boolean | null;
  pagination?: { pageSize?: number; pageNumber?: number } | null;
  orderBy?: { field?: string; descending?: boolean }[] | null;
}

export class CorporateUserService {
  private static readonly BASE = '/api/CorporateUser';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getAll(query?: CorporateUserQuery): Promise<ServiceResultT<CorporateUserViewModel[]>> {
    const params: Record<string, unknown> = {};
    if (query?.Email != null) params.Email = query.Email;
    if (query?.CompanyName != null) params.CompanyName = query.CompanyName;
    if (query?.TaxNumber != null) params.TaxNumber = query.TaxNumber;
    if (query?.IsEmailConfirmed != null) params.IsEmailConfirmed = query.IsEmailConfirmed;
    Object.assign(params, buildPaginationParams(query?.pagination ?? undefined));
    if (query?.orderBy?.length) {
      const orderByDescriptor = query.orderBy.map((o) => ({
        field: o.field ?? '',
        descending: o.descending ?? false,
      }));
      Object.assign(params, buildOrderByParams(orderByDescriptor));
    }
    const { data } = await this.client.get<ServiceResultT<CorporateUserViewModel[]>>(
      CorporateUserService.BASE,
      { params }
    );
    return data;
  }

  async updateMe(
    payload: CorporateUserProfileUpdateRequest,
    taxCertificate?: File | null
  ): Promise<ServiceResultT<CorporateUserProfileViewModel>> {
    if (taxCertificate) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value != null && value !== '') {
          formData.append(key, String(value));
        }
      });
      formData.append('taxCertificate', taxCertificate);
      const { data } = await this.client.put<ServiceResultT<CorporateUserProfileViewModel>>(
        `${CorporateUserService.BASE}/me`,
        formData
      );
      return data;
    }

    const { data } = await this.client.put<ServiceResultT<CorporateUserProfileViewModel>>(
      `${CorporateUserService.BASE}/me`,
      payload
    );
    return data;
  }
}

export const corporateUserService = new CorporateUserService();
